import { DateTime } from 'luxon'
import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom'

// components
import FullScreenLoader from '../../components/FullScreenLoader/FullScreenLoader.component'
import ImagesGallery, { GalleryImage } from '../../components/ImagesGallery/ImagesGallery.component'
import LoadingAnimation from '../../components/LoadingAnimation/LoadingAnimation.component'
import StickyMobileSection from '../../components/StickyMobileSection/StickyMobileSection.component'
import AdditionalInformation from './components/AdditionalInformation/AdditionalInformation.component'
import NextButton from './components/NextButton/NextButton.component'
import LongDuration from './LongDuration.view'
import Overview from './Overview.view'
import ShortDuration from './ShortDuration.view'

// utils
import { useGetUser } from '../../hooks/useAuth'
import { dateToLocaleString } from '../../utils/dateToLocaleString.utils'
import { useLot } from './hooks/useLot'
import { getLotDurationType } from './utils/getLotDurationType.utils'
import { getLotRoutingState } from './utils/getLotRoutingState.utils'

// routes
import { lotRouteNotFound, lotRouteSubscriptions, lotRouteNotAvailable, lotFeatureQueryParam } from './route'
import { checkoutRouteVehicleStep } from '../Checkout/route'
import { lotRoute, lotRouteLongDuration, lotRouteNightDuration, lotRouteShortDuration } from './route'

import { LotDurationType, LotRoutingState, LotViewName } from '../../types/lot.interface'
import { LotPricing } from '../../types/userData.interface'
import NightParking from './components/NightParking/NightParking.component'
import { LotTitle, StickySection, Line, Container, LoadingAnimationWrapper } from './Lot.style'
import Subscriptions from './components/Subscriptions/Subscriptions.component'
import LotOutsideDates from './components/LotOutsideDates/LotOutsideDates.component'
import { getIsoDurationInDays } from '../../utils/getMinOrMaxFromISODuration.utils'
import useLanguage from '@hooks/useLanguage.hook'
import { Helmet } from 'react-helmet-async'
import { getLanguageBasedOnDomain } from '@utils/getDomainExtension'
import { parseLotImages } from './utils/parseLotImages.utils'

const Lot: FC = () => {
    const { t, i18n } = useTranslation()
    const [lng] = useLanguage()
    const seoLang = i18n.getFixedT(getLanguageBasedOnDomain())
    const history = useHistory()
    const { pathname, search } = useLocation()
    const { lotId } = useParams() as { lotId: string }
    const { user } = useGetUser()
    const [lotImages, setLotImages] = useState<GalleryImage[]>([])
    const feature = new URLSearchParams(search).get(lotFeatureQueryParam)

    const isDiscountApplied = user?.private_pricing_lots
        ?.map((l: LotPricing) => l.lot_id.toLowerCase())
        .includes(lotId.toLowerCase())
    const bookingType = getLotDurationType(pathname)
    const isNightParking = bookingType === LotDurationType.NIGHT

    const [nextButtonVisible, setNextButtonVisible] = useState(false)

    const { lot, error: lotError } = useLot(lotId)

    const langParts = lng?.toLowerCase().split('-') || []
    const fullLocale = langParts.join('-') // e.g. 'de-ch'
    const baseLang = langParts[0] || 'en'

    const description =
        lot && lot.description ? lot.description[fullLocale] || lot.description[baseLang] || lot.description['en'] : ''
    const minBookingDays = getIsoDurationInDays(lot?.min_booking_duration ?? 'P1D')
    const maxBookingDays = getIsoDurationInDays(lot?.max_booking_duration ?? 'P365D')

    const allEnabledFeatures = lot?.active_features.filter((activeFeature) => activeFeature.enabled)

    const onCheckoutClick = () => {
        history.push(checkoutRouteVehicleStep())
    }

    useEffect(() => {
        let state
        let newPathname
        const lotRoutingState = getLotRoutingState(lot, lotError)
        setNextButtonVisible(true)

        if (!lot) {
            setNextButtonVisible(false)
        }

        switch (lotRoutingState) {
            case LotRoutingState.GENERIC_ERROR:
                history.push(lotRouteNotFound())
                break
            case LotRoutingState.NOT_LIVE_ERROR:
                newPathname = lotRouteNotFound()
                state = { errorMessage: t('404.lotNotFound', { lotname: lot?.name }) }
                history.push(newPathname, state)
                break
            case LotRoutingState.LOT_END_OF_LIVE_ERROR:
                newPathname = lotRouteNotAvailable(lot?.id)
                state = {
                    errorMessage: lot
                        ? t('404.earliestBookingDateInPast', {
                              earliest: dateToLocaleString(DateTime.fromISO(lot?.earliest_booking_date)),
                              latest: dateToLocaleString(DateTime.fromISO(lot?.end_of_live_date))
                          })
                        : {}
                }
                setNextButtonVisible(false)
                history.replace(newPathname, state)
                break
            case LotRoutingState.LOT_HOURLY_TOO_EARLY_ERROR:
                newPathname = lotRouteNotAvailable(lot?.id)
                state = {
                    errorMessage: lot
                        ? t('404.earliestBookingDateInFuture', {
                              earliest: dateToLocaleString(DateTime.fromISO(lot.earliest_booking_date))
                          })
                        : {}
                }
                setNextButtonVisible(false)
                history.replace(newPathname, state)
                break
            case LotRoutingState.DAILY:
                history.replace(lotRouteLongDuration(lot?.id))
                break
            case LotRoutingState.HOURLY:
                history.replace(lotRouteShortDuration(lot?.id))
                break

            case LotRoutingState.SUBSCRIPTION: {
                history.replace(lotRouteSubscriptions(lot?.id))
                break
            }
            case LotRoutingState.NIGHTLY:
                history.replace(lotRouteNightDuration(lot?.id))
                break
            default:
                setNextButtonVisible(false)
                break
        }
    }, [t, lot, history, lotError, feature])

    useEffect(() => {
        if (!lot) return
        if (pathname === lotRouteNotAvailable(lot.id)) return
        if (lot.image_urls) {
            setLotImages(parseLotImages(lot.image_urls, lot.name, lot.cityName || '', t))
        }
        setNextButtonVisible(feature !== null)
    }, [t, pathname, lot, feature, setNextButtonVisible])

    if (!lot) return <FullScreenLoader />

    return (
        <>
            <Helmet>
                <title>
                    {seoLang('seo.lot.title', {
                        name: lot.name,
                        city: lot.cityName
                    })}
                </title>
                <meta
                    name="description"
                    content={seoLang('seo.lot.description', {
                        city: lot.cityName,
                        address: `${lot.address}, ${lot.city}`
                    })}
                />
            </Helmet>
            <div>
                {lot.banner ? (
                    <ImagesGallery images={lotImages} />
                ) : (
                    <LoadingAnimationWrapper>
                        <LoadingAnimation />
                    </LoadingAnimationWrapper>
                )}
                <Container>
                    <LotTitle>{lot.name}</LotTitle>
                    <Switch>
                        {/* old redirect logic */}
                        <Route path={lotRouteNotAvailable(lot.id)} exact component={LotOutsideDates} />
                        <Route path={`${lotRoute(lot.id)}/short`}>
                            <Redirect to={`${lotRouteShortDuration(lot.id)}`} />
                        </Route>
                        <Route path={`${lotRoute(lot.id)}/long`}>
                            <Redirect to={`${lotRouteLongDuration(lot.id)}`} />
                        </Route>
                        <Route path={`${lotRoute(lot.id)}/night`}>
                            <Redirect to={`${lotRouteNightDuration(lot.id)}`} />
                        </Route>
                        <Route path={`${lotRoute(lot.id)}/subscription`}>
                            <Redirect to={`${lotRouteSubscriptions(lot.id)}`} />
                        </Route>
                    </Switch>
                    {feature === null && <Overview />}
                    {feature === LotDurationType.SHORT && <ShortDuration />}
                    {feature === LotDurationType.LONG && <LongDuration />}
                    {feature === LotDurationType.NIGHT && <NightParking />}
                    {feature === LotDurationType.SUBSCRIPTION && <Subscriptions />}
                    <Line />
                    <AdditionalInformation
                        lotId={lot.id}
                        address={lot.address}
                        city={lot.city}
                        prices={lot.prices}
                        enabledFeatures={allEnabledFeatures || []}
                        description={description}
                        showDiscount={isDiscountApplied && !isNightParking ? true : false}
                        minBookingDurationInDays={minBookingDays}
                        maxBookingDurationInDays={maxBookingDays}
                        openingHours={lot.opening_hours.opening_hours_rrules}
                    />
                </Container>
            </div>
            {nextButtonVisible && (
                <StickyMobileSection>
                    <StickySection>
                        <NextButton
                            isMobile
                            translateKey="checkout.payment.continueWithVehicleData"
                            onCheckoutClick={onCheckoutClick}
                        />
                    </StickySection>
                </StickyMobileSection>
            )}
        </>
    )
}

export default Lot
